import React, { SyntheticEvent, useEffect, useState } from 'react';
import styles from './DateFilter.module.scss';
import filtersStyles from '../../Filters.module.scss';
import { format, subDays, startOfDay, endOfDay, addMonths, endOfYear, isValid } from 'date-fns';
import { Action, ClickDetector, CloseLight, DateRange, Select } from 'esa-ui-library';
import { PropertyType, useStore, TIMEZONES } from '../../../../../common';
import { find } from 'lodash';
import useFilters from '../../../../../common/hooks/useFilters';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';

interface DateFilterProps {
	fullWidth?: boolean;
}

const DateFilter = ({ fullWidth }: DateFilterProps): JSX.Element => {
	const [{ filters }] = useStore();
	const [opened, setOpened] = useState(false);
	const [timezone, setTimezone] = useState(TIMEZONES[0]);
	const [start, setStart] = useState(subDays(startOfDay(new Date()), 6));
	const [end, setEnd] = useState(endOfDay(new Date()));
	const [activeLabel, setActiveLabel] = useState('');
	const { onFilter } = useFilters();

	const setCurrent = (dates: PropertyType) => {
		const { startDate, endDate, customRange } = dates;
		setStart(startDate);
		setEnd(endDate);
		if (customRange) return setActiveLabel(customRange);
		setActiveLabel(`${format(startDate, 'd MMM yyyy')} - ${format(endDate, 'd MMM yyyy')}`);
	};

	const onDateChange = (dates: PropertyType) => {
		setCurrent(dates);
		const utcStart = zonedTimeToUtc(dates.startDate, timezone.value);
		const utcEnd = zonedTimeToUtc(dates.endDate, timezone.value);

		const filterValue = [
			{
				name: 'start_date',
				value: utcStart.toISOString(),
			},
			{
				name: 'end_date',
				value: utcEnd.toISOString(),
			},
		];
		onFilter(filterValue);
		setOpened(false);
	};

	const onTimezoneChange = (tz: any) => {
		const selectedTz = Array.isArray(tz) ? tz[0] : tz;
		setTimezone(selectedTz);
		const utcStart = zonedTimeToUtc(start, selectedTz.value);
		const utcEnd = zonedTimeToUtc(end, selectedTz.value);

		const filterValue = [
			{
				name: 'start_date',
				value: utcStart.toISOString(),
			},
			{
				name: 'end_date',
				value: utcEnd.toISOString(),
			},
		];
		onFilter(filterValue);
	};

	const onDateRemove = (e: SyntheticEvent | null) => {
		e?.stopPropagation();

		const defaultStart = subDays(startOfDay(new Date()), 6);
		const defaultEnd = endOfDay(new Date());

		setStart(defaultStart);
		setEnd(defaultEnd);
		setActiveLabel('');
		const filterValue = [
			{ name: 'start_date', value: '' },
			{ name: 'end_date', value: '' },
		];

		onFilter(filterValue);
	};

	const getLabelContent = () => {
		return (
			<>
				{activeLabel} {timezone.value !== 'UTC' && `(${timezone.label})`}
				<i className={filtersStyles.ClearFilter} onClick={onDateRemove}>
					<CloseLight size={16} />
				</i>
			</>
		);
	};

	useEffect(() => {
		const filterStart = find(filters, ['name', 'start_date'])?.value;
		const filterEnd = find(filters, ['name', 'end_date'])?.value;

		if (filterStart && filterEnd) {
			const filterDateStart = new Date(filterStart);
			const filterDateEnd = new Date(filterEnd);

			const startDate = utcToZonedTime(isValid(filterDateStart) ? filterDateStart : filterStart, timezone.value);
			const endDate = utcToZonedTime(isValid(filterDateEnd) ? filterDateEnd : filterEnd, timezone.value);
			setStart(startDate);
			setEnd(endDate);
			setActiveLabel(`${format(startDate, 'd MMM yyyy')} - ${format(endDate, 'd MMM yyyy')}`);
		} else {
			setStart(subDays(startOfDay(new Date()), 6));
			setEnd(endOfDay(new Date()));
			setActiveLabel('');
		}
	}, [filters]);

	return (
		<section style={fullWidth ? { width: '100%' } : {}} className={styles.DateFilter}>
			<ClickDetector onOutsideClick={() => setOpened(false)} dataId='detector'>
				<Action
					select
					label='Date Range'
					labelContent={activeLabel ? getLabelContent() : ''}
					fluidHeight
					className={`${filtersStyles.Filter} ${styles.Filter} 
				${activeLabel.length ? filtersStyles.Active : ''} `}
					dropdownClass={styles.DateDropdown}
					onDropdownChange={setOpened}
					controlled
					opened={opened}
					dataId='date-filter'
				>
					<section onClick={(e) => e.stopPropagation()}>
						<div className={styles.TimezoneSelector}>
							<Select
								label='Timezone'
								options={TIMEZONES}
								selected={[timezone]}
								onChange={onTimezoneChange}
								dataId='timezone-select'
								small
							/>
						</div>
						<DateRange
							start={start}
							end={end}
							onChange={onDateChange}
							onCancel={() => setOpened(false)}
							months={2}
							maxDate={endOfYear(addMonths(new Date(), 6))}
							customRanges
							time
						/>
					</section>
				</Action>
			</ClickDetector>
		</section>
	);
};

export { DateFilter };
